import React, { useState } from "react";
import moment from "moment";
import { formatCurrency } from "../../helper/formatCurrency";
import {
  APPOINTMENT_PAYMENT_STATUS,
  isEndStatusCancelled,
} from "../../helper/appointmentConstants";

// Shared unified payment / refund "event history" table. Extracted from
// EventHistoryPaymentDetails so it can be reused wherever a hydrated
// `eventHistory` log needs to be rendered (navigo appointment payments and the
// cart verification screen's Payment details). Both `buildEventRows` (the wire
// → row mapper) and the `EventHistoryTable` renderer live here so the two
// screens stay pixel-identical from a single source of truth.

// Column template for the unified event-history table. Mirrors the screenshot:
// [type] | Amount | Order ID/Refund ID | Payment ID | Transaction date & time |
// Refund initiated by | Status.
const EVENT_TABLE_COLUMNS = "1.6fr 1fr 2fr 2fr 2.2fr 1.6fr 1.4fr";

const EVENT_TABLE_HEADERS = [
  "Payment method",
  "Amount",
  "Order ID/Refund ID",
  "Payment ID",
  "Transaction date & time",
  "Refund initiated by",
  "Status",
];

// Lifecycle status text shared by payments and refunds. The eventHistory is an
// append-only log, so a single transaction can move INITIATED -> SUCCESS; we map
// each terminal/intermediate state to the same labels the ops team already sees.
function eventStatusName(status) {
  switch (status) {
    case "SUCCESS":
      return "Successful";
    case "FAILED":
      return "Failed";
    case "INITIATED":
    case "IN_PROGRESS":
    case "PENDING":
      return "In progress";
    default:
      return status || "-";
  }
}

// Payment statuses meaning the link behind this money will never be paid.
const DEAD_LINK_STATUSES = [
  APPOINTMENT_PAYMENT_STATUS.FAILED,
  APPOINTMENT_PAYMENT_STATUS.LINK_EXPIRED,
  APPOINTMENT_PAYMENT_STATUS.LINK_CANCELLED,
];

// Statuses the hydrator emits for money that has not settled yet.
const UNSETTLED = ["INITIATED", "IN_PROGRESS", "PENDING"];

// Resolve a PAYMENT leg's label, downgrading a stuck "In progress" once we know the link is dead.
//
// eventHistory mirrors the pg-order ledger, and pg-order is not always told that a link died:
// payment-service's expiry/cancel handler historically never marked the EXTERNAL transaction
// failed, so the order sat at INITIATED forever and this table showed "In progress" indefinitely
// (see the payment-service fix in cancelPaymentLinkAppointment). That is now settled at source,
// but every appointment stuck before that fix shipped still carries the stale row — hence this
// fallback, which reads the appointment's own paymentStatusResponse instead of trusting the leg.
//
// A wallet leg on a cancelled appointment reads "Released", not "Unsuccessful": the block is
// returned to the patient, not consumed. Same wording buildPaymentRows already uses.
function resolvePaymentStatus(rawStatus, channel, context) {
  const name = eventStatusName(rawStatus);
  if (!context) return name;
  if (!UNSETTLED.includes((rawStatus || "").toUpperCase())) return name;
  if (!DEAD_LINK_STATUSES.includes(context.paymentStatusResponse)) return name;

  if (channel === "WALLET" && isEndStatusCancelled(context.appointmentStatus)) {
    return "Released";
  }
  return "Unsuccessful";
}

// Hover card carrying the remark the operator typed into the refund dialog
// ("Remark related to refund" → pg-order Refund.reason → `refund.initiatedByComment`).
//
// Positioned with `fixed` and coordinates read off the icon at hover time, rather than
// `absolute` inside the cell: the table wrapper below sets `overflow: hidden` to clip its
// rounded corners, which would swallow any bubble anchored within a cell. Clamped against
// the viewport's right edge so a long remark in the rightmost columns stays on screen.
const RefundCommentIcon = ({ comment }) => {
  const [anchor, setAnchor] = useState(null);

  const show = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setAnchor({
      top: rect.bottom + 6,
      left: Math.max(8, Math.min(rect.left, window.innerWidth - 288)),
    });
  };

  return (
    <>
      <span
        onMouseEnter={show}
        onMouseLeave={() => setAnchor(null)}
        title="Remark added while refunding"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: "15px",
          height: "15px",
          flex: "0 0 auto",
          borderRadius: "50%",
          border: "1px solid #8a8a8a",
          color: "#8a8a8a",
          fontSize: "10px",
          fontWeight: 700,
          fontStyle: "italic",
          fontFamily: "Georgia, serif",
          cursor: "help",
          userSelect: "none",
        }}
      >
        i
      </span>
      {anchor && (
        <div
          style={{
            position: "fixed",
            top: `${anchor.top}px`,
            left: `${anchor.left}px`,
            zIndex: 1000,
            maxWidth: "280px",
            padding: "8px 10px",
            background: "#333333",
            color: "#ffffff",
            borderRadius: "4px",
            fontFamily: "Montserrat",
            fontSize: "12px",
            fontWeight: 400,
            lineHeight: 1.45,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
            pointerEvents: "none",
          }}
        >
          {comment}
        </div>
      )}
    </>
  );
};

// Actor-type fallbacks that stand for Zyla itself rather than for an identifiable person:
// a vendor-side or doctor-side trigger, or an automatic service refund. Matched against the
// backend's typed fallbacks (composeRefundInitiator), which are either the bare label or the
// label plus the actor's id ("Doctor #42") when the monolith name lookup came back empty.
// A refund whose creator *did* resolve to a real name is left alone.
const ZYLA_INITIATOR_LABELS = ["Vendor", "Doctor", "System"];

function displayInitiator(name) {
  const isZyla = ZYLA_INITIATOR_LABELS.some(
    (label) => name === label || name.startsWith(`${label} #`),
  );
  return isZyla ? "Zyla" : name;
}

// "Refund initiated by" cell. The backend hands us a display-ready label — a resolved
// employee/doctor name, or a typed fallback like "Employee #42" / "Patient" / "System" when
// the monolith name lookup was skipped or failed. It is never a bare id, so it renders
// verbatim apart from the Zyla collapse above. Payments have no initiator concept and always
// collapse to "-".
function initiatedByCell(refundDetail) {
  const name = displayInitiator((refundDetail?.initiatedBy || "").trim());
  const comment = (refundDetail?.initiatedByComment || "").trim();
  if (!name && !comment) return "-";

  return (
    <span
      style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
      key="initiated-by"
    >
      <span>{name || "-"}</span>
      {comment ? <RefundCommentIcon comment={comment} /> : null}
    </span>
  );
}

function channelLabel(type, channel) {
  if (type === "REFUND") {
    return channel === "WALLET"
      ? "Refund to Wallet"
      : "Refund to Razor pay/Account";
  }
  return channel === "WALLET" ? "Payment via wallet" : "Payment via Razor pay";
}

// Flatten the hydrated eventHistory into table rows. New wire format (see backend
// EventHydrator): each event carries its id at the top level (`event.id`), its
// timestamp at `event.occurredAt`, and the per-event detail (amounts, method, status)
// inside `event.payment` / `event.refund`. Status now lives on the detail
// (`detail.status`), not the event. A BOTH-method event is split into a wallet row and
// an external (Razorpay) row so amounts line up with the screenshot. The backend already
// returns events newest-first (descending by occurredAt), so we preserve that order.
//
// `context` is optional — `{ paymentStatusResponse, appointmentStatus }` from the appointment
// this history belongs to. When supplied it lets a stuck-pending payment leg be reported against
// the appointment's real state (see resolvePaymentStatus). The cart verification screen renders a
// cart's history with no appointment behind it and correctly passes nothing.
//
// Cells are usually strings, but "Refund initiated by" returns a React node (name + remark
// icon). The renderer drops each cell straight into a div, so both are fine — but anything
// else consuming these rows (an export, say) must handle nodes rather than assume strings.
export function buildEventRows(eventHistory, context) {
  if (!Array.isArray(eventHistory)) return [];

  const rows = [];

  eventHistory.forEach((event) => {
    const isRefund = event?.type === "REFUND";
    const detail = isRefund ? event?.refund : event?.payment;
    if (!detail) return;

    const id = event?.id || "-";
    const wallet = Number(detail.walletAmount) || 0;
    const external = Number(detail.externalAmount) || 0;

    const channels = [];
    if (wallet > 0) channels.push({ channel: "WALLET", amount: wallet });
    if (external > 0) channels.push({ channel: "EXTERNAL", amount: external });
    if (channels.length === 0) {
      // Amounts missing/zero per channel — fall back to the method + total amount.
      channels.push({
        channel: detail.method === "WALLET" ? "WALLET" : "EXTERNAL",
        amount: Number(detail.amount) || 0,
      });
    }

    const sign = isRefund ? "+" : "-";
    channels.forEach((c) => {
      // Payment ID only exists for the external (Razorpay) leg of a payment —
      // the wallet leg and refunds have no gateway payment id.
      const paymentId =
        !isRefund && c.channel === "EXTERNAL"
          ? detail.externalPaymentId || "-"
          : "-";
      rows.push([
        channelLabel(event.type, c.channel),
        `${sign}${formatCurrency(c.amount)}`,
        id,
        paymentId,
        event.occurredAt
          ? moment(event.occurredAt).format("DD MMM YYYY  hh:mm A")
          : "-",
        isRefund ? initiatedByCell(detail) : "-",
        isRefund
          ? eventStatusName(detail.status)
          : resolvePaymentStatus(detail.status, c.channel, context),
      ]);
    });
  });

  return rows;
}

export const EventHistoryTable = ({ rows }) => {
  const cellBase = {
    padding: "10px",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontWeight: 500,
    color: "#202020",
    borderRight: "1px solid #d3d3d3",
    borderBottom: "1px solid #d3d3d3",
    background: "#fff",
  };
  const columnCount = EVENT_TABLE_HEADERS.length;

  return (
    <div
      style={{
        border: "1px solid #d3d3d3",
        borderRadius: "6px",
        overflow: "hidden",
        width: "100%",
        fontFamily: "sans-serif",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: EVENT_TABLE_COLUMNS,
          background: "#f8f8f8",
          fontWeight: 600,
          fontFamily: "Montserrat",
          fontSize: "12px",
          color: "#666",
          borderBottom: "1px solid #d3d3d3",
        }}
      >
        {EVENT_TABLE_HEADERS.map((header, index) => (
          <div
            key={`header-${index}`}
            style={{
              padding: "10px",
              borderRight:
                index === columnCount - 1 ? "none" : "1px solid #d3d3d3",
            }}
          >
            {header}
          </div>
        ))}
      </div>

      {/* Body */}
      <div
        style={{ display: "grid", gridTemplateColumns: EVENT_TABLE_COLUMNS }}
      >
        {rows.length > 0 ? (
          rows.map((row, rowIndex) =>
            row.map((cell, cellIndex) => {
              const isLastCol = cellIndex === columnCount - 1;
              const isLastRow = rowIndex === rows.length - 1;
              return (
                <div
                  key={`${rowIndex}-${cellIndex}`}
                  style={{
                    ...cellBase,
                    borderRight: isLastCol ? "none" : cellBase.borderRight,
                    borderBottom: isLastRow ? "none" : cellBase.borderBottom,
                  }}
                >
                  {cell}
                </div>
              );
            }),
          )
        ) : (
          <div
            style={{
              gridColumn: "1 / -1",
              textAlign: "center",
              padding: "12px",
              color: "#999",
            }}
          >
            No payment data available
          </div>
        )}
      </div>
    </div>
  );
};
